import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Lock, Shield } from 'lucide-react';
import { useAuthStatus } from '../hooks/useAuthStatus';
import tyrlLogo from '../assets/tyrl-logo.svg';

// Client-facing landing for OrgIQ Hub. Mirrors the structure of the
// internal-facing ConnectPage but trimmed down for invited client admins:
// no portfolio-org URL input, no "your client's org" framing, no "Internal"
// version chip. A previously-invited client admin who lands here will OAuth
// through /api/auth/connect; the server checks hub_user on the way back and
// auto-stamps role=client-admin if their SF user has activated an invite
// before, so they re-enter at /hub rather than /internal.
export function HubLoginPage() {
  const navigate = useNavigate();
  const { status } = useAuthStatus();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);

  // Surface the callback-side rejection reasons. The /callback endpoint
  // bounces back here with ?error=not-invited (no hub_user row) or
  // ?error=access-revoked (deactivated) so the visitor sees a real
  // explanation instead of silently failing into the consultant flow.
  const errorParam = searchParams.get('error');

  useEffect(() => {
    if (!status?.connected) return;
    // A connected consultant who lands here goes to the client scans, not
    // to the client login screen. Client admins go to /hub. A
    // session with no role is neither, so it stays and signs in again.
    if (status.role === 'client-admin') navigate('/hub', { replace: true });
    else if (status.role === 'consultant') navigate('/internal/leads', { replace: true });
  }, [status, navigate]);

  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // intent=client-admin tells the server callback to look this user up
    // in hub_user and either route them to /hub (if active) or bounce
    // back here with an error reason (if not invited or deactivated).
    // Without it, a non-invited SF user would silently fall through to
    // the consultant flow.
    const qs = new URLSearchParams({ intent: 'client-admin' });
    window.location.href = `/api/auth/connect?${qs.toString()}`;
  };

  return (
    <div className="relative min-h-screen bg-surface-0 text-crux-light overflow-hidden">
      {/* pillar gradient line */}
      <div
        className="fixed top-0 inset-x-0 h-[2px] z-50"
        style={{
          background:
            'linear-gradient(90deg, #3D55D6 0%, #5B7CFF 33%, #9A988F 66%, #3FA96B 100%)',
        }}
      />

      {/* faint grid background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(27,27,27,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(27,27,27,0.05) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* top bar */}
      <header className="fixed top-[2px] inset-x-0 z-40 px-10 py-5 flex items-center justify-between">
        <img src={tyrlLogo} alt="TYRL" className="h-6 opacity-90 brightness-0 invert" />
        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-neutral-500">
          v2.0 · Hub
        </div>
      </header>

      {/* main */}
      <main className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-[440px] bg-surface-2 border border-border rounded-2xl overflow-hidden"
        >
          {/* card header */}
          <div className="px-9 pt-9 pb-7 border-b border-border">
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-3.5"
              style={{ color: '#3FA96B' }}
            >
              OrgIQ Hub
            </div>
            <h1 className="text-[42px] font-bold tracking-[-0.04em] leading-none text-crux-light mb-2.5">
              Welcome back.
            </h1>
            <p className="text-sm text-neutral-400 leading-snug">
              Your Salesforce health, at a glance. Sign in with your org to continue.
            </p>
          </div>

          {errorParam ? (
            <div className="px-9 pt-5">
              <div className="px-3 py-2.5 rounded-md border border-sunset/40 bg-sunset/10 text-xs text-sunset">
                {errorParam === 'not-invited' && (
                  <>
                    <strong className="font-semibold">No Hub access on file.</strong> OrgIQ Hub
                    is invite-only. Ask your TYRL team to send you an invitation.
                  </>
                )}
                {errorParam === 'access-revoked' && (
                  <>
                    <strong className="font-semibold">Access has been revoked.</strong> Contact
                    your TYRL team if you believe this is in error.
                  </>
                )}
                {errorParam !== 'not-invited' && errorParam !== 'access-revoked' && (
                  <>Sign-in failed. Try again or contact your TYRL team.</>
                )}
              </div>
            </div>
          ) : null}

          {/* form */}
          <form onSubmit={handleConnect} className="px-9 pt-7 pb-8 flex flex-col gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 mt-1 bg-sfblue hover:opacity-90 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-[10px] flex items-center justify-center gap-2.5 transition-all tracking-[0.01em]"
            >
              <ArrowRight size={16} className="shrink-0" />
              {submitting ? 'Redirecting to Salesforce…' : 'Sign in with Salesforce'}
            </button>

            <p className="text-[11px] text-neutral-500 text-center leading-relaxed pt-1">
              First time here? OrgIQ Hub access is invite-only, and your TYRL team
              can send you an invitation.
            </p>

            <div className="flex items-center justify-center gap-4 pt-1">
              <SecurityItem icon={<Shield size={12} />}>Secure OAuth 2.0</SecurityItem>
              <SecurityItem icon={<Clock size={12} />}>Read-only</SecurityItem>
              <SecurityItem icon={<Lock size={12} />}>Tokens never leave the server</SecurityItem>
            </div>
          </form>
        </motion.div>
      </main>

      {/* footer */}
      <footer className="fixed bottom-0 inset-x-0 z-10 px-10 py-5 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-500">
          Growth. By design.
        </div>
        <div className="flex gap-1.5">
          <span className="w-5 h-[2px] rounded-[1px] bg-design" />
          <span className="w-5 h-[2px] rounded-[1px] bg-deliver" />
          <span className="w-5 h-[2px] rounded-[1px] bg-adopt" />
          <span className="w-5 h-[2px] rounded-[1px] bg-grow" />
        </div>
      </footer>
    </div>
  );
}

function SecurityItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
      <span className="shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}
